import { makeAutoObservable } from 'mobx';
import { apiRequest } from '../helpers/request';
import { REQUEST_TYPE } from '../constants/base';
import { notificationStore } from './Notification';
import AuthStore from './Auth';

export const ALERT_MESSAGES = {
  PROCESSING:
    'Is not possible to process this request now. We are processing previous request.',
  PREVIEW_REQUIRED: 'You need to run preview first.',
  ERROR_PROCESSING: 'Error while processing request.',
};

class DataStore {
  isLoading = false;
  isProcessing = false;
  gettingVersion = false;
  isPipelinePreview = false;
  isTerminatePreview = false;
  isStandalonePreview = false;
  isDownloadPreview = false;
  isDownloadProcessing = false;
  isGettingInfo = false;
  standaloneHelper: any = null;
  pipelineCredits: number = 0;
  internalUserInfo: any = null;
  config: any = null;
  backendVersion: string = '';

  output: any = '';

  constructor() {
    makeAutoObservable(this);
  }

  setIsGettingInfo(isGettingInfo: boolean) {
    this.isGettingInfo = isGettingInfo;
  }

  setStandaloneHelper(data: any) {
    this.standaloneHelper = data;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setProcessing(isProcessing: boolean) {
    this.isProcessing = isProcessing;
  }

  setPipelinePreview(isPipelinePreview: boolean) {
    this.isPipelinePreview = isPipelinePreview;
  }

  setTerminatePreview(isTerminatePreview: boolean) {
    this.isTerminatePreview = isTerminatePreview;
  }

  setStandalonePreview(isStandalonePreview: boolean) {
    this.isStandalonePreview = isStandalonePreview;
  }

  setDownloadPreview(isDownloadPreview: boolean) {
    this.isDownloadPreview = isDownloadPreview;
  }

  setDownloadProcessing(isDownloadProcessing: boolean) {
    this.isDownloadProcessing = isDownloadProcessing;
  }

  setOutput(output: any) {
    this.output = output;
  }

  showOutput(request: string, data: any, type: string = '') {
    this.setOutput({ request: request ?? '', response: data ?? '', type });
  }

  setCredits(credits: number) {
    this.pipelineCredits = credits;
  }

  setGettingVersion(isGettingVersion: boolean) {
    this.gettingVersion = isGettingVersion;
  }

  setBackendVersion(version: string) {
    this.backendVersion = version;
  }

  setInternalUserInfo(data: any) {
    this.internalUserInfo = data.response;
  }

  setConfig(config: any) {
    this.config = config;
  }

  resetPreviewAndProcessing() {
    if (this.isProcessing) {
      return false;
    }
    this.setPipelinePreview(false);
    this.setTerminatePreview(false);
    this.setStandalonePreview(false);
    this.setDownloadPreview(false);
    this.setCredits(0);
  }

  async getBackendVersion() {
    if (this.backendVersion !== '' || this.gettingVersion) {
      return;
    }
    this.setGettingVersion(true);
    try {
      const res = await apiRequest('/version', REQUEST_TYPE.GET, null);

      if (res.response.backendVersion) {
        this.setBackendVersion(res.response.backendVersion);
      }
      this.setGettingVersion(false);
      return;
    } catch (error) {
      console.log('Error fetching backend version:', error);
      this.setGettingVersion(false);
      return;
    }
  }

  async pipelinePreviewRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setCredits(0);
    this.setProcessing(true);
    try {
      const res = await apiRequest(
        '/pipeline/preview',
        REQUEST_TYPE.POST,
        data
      );

      this.setProcessing(false);
      if (res.messageType === 'error') {
        this.setPipelinePreview(false);
      } else {
        this.setPipelinePreview(true);
      }

      if (res?.cost) {
        this.setCredits(res.cost);
      }

      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
      this.setPipelinePreview(false);
    }
    return null;
  }

  async pipelineLaunchRequest(data: any = {}) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    if (!this.isPipelinePreview) {
      notificationStore.showMessage(ALERT_MESSAGES.PREVIEW_REQUIRED, 'info');
      return false;
    }
    try {
      this.setProcessing(true);
      const res = await apiRequest('/pipeline/launch', REQUEST_TYPE.PUT, {
        launch: true,
        ...data,
      });
      if (res.messageType !== 'confirm') {
        this.setPipelinePreview(false);
      }

      this.setProcessing(false);
      this.getUserInternalInfo();
      return res;
    } catch (error) {
      this.setProcessing(false);
      this.setPipelinePreview(false);
    }
  }

  async terminatePreviewRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      const res = await apiRequest(
        '/terminate/preview',
        REQUEST_TYPE.POST,
        data
      );
      if (res.response) {
        this.setTerminatePreview(true);
      }
      this.setProcessing(false);
      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.PREVIEW_REQUIRED, 'info');
      this.setProcessing(false);
      this.setTerminatePreview(false);
    }
    return null;
  }

  async terminateLaunchRequest() {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    if (!this.isTerminatePreview) {
      notificationStore.showMessage(ALERT_MESSAGES.PREVIEW_REQUIRED, 'info');
      return false;
    }
    try {
      this.setProcessing(true);
      await apiRequest('/terminate/launch', REQUEST_TYPE.PUT, { launch: true });

      this.showOutput('Terminate launch', {});
      this.setTerminatePreview(false);
      this.setProcessing(false);
    } catch (error) {
      this.setTerminatePreview(false);
      this.setProcessing(false);
    }
  }

  async metricsLaunchRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      return false;
    }
    this.setProcessing(true);
    try {
      const res = await apiRequest('/metrics/launch', REQUEST_TYPE.POST, data);
      this.setProcessing(false);
      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
      return null;
    }
  }

  async searchLaunchRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      const res = await apiRequest('/search/launch', REQUEST_TYPE.POST, data);
      this.setProcessing(false);
      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
    }
  }

  async jobsOverviewLaunchRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      const res = await apiRequest(
        '/jobsOverview/launch',
        REQUEST_TYPE.POST,
        data
      );
      this.setProcessing(false);

      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
    }
    return null;
  }

  async standalonePreviewRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setStandalonePreview(false);
    this.setProcessing(true);
    try {
      const res = await apiRequest(
        '/standalone/preview',
        REQUEST_TYPE.POST,
        data
      );

      if (res.response) {
        this.setStandalonePreview(true);
      }
      this.setProcessing(false);

      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
      this.setStandalonePreview(false);
    }

    return null;
  }

  async standaloneLaunchRequest() {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    if (!this.isStandalonePreview) {
      notificationStore.showMessage(ALERT_MESSAGES.PREVIEW_REQUIRED, 'info');
      return false;
    }
    try {
      this.setProcessing(true);
      await apiRequest('/standalone/launch', REQUEST_TYPE.PUT, {
        launch: true,
      });

      this.showOutput('Standalone launch', {});
      this.setStandalonePreview(false);
      this.setProcessing(false);
    } catch (error) {
      this.setProcessing(false);
      this.setStandalonePreview(false);
    }
  }

  async downloadPreviewRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    this.setDownloadPreview(false);
    try {
      const res = await apiRequest(
        '/download/preview',
        REQUEST_TYPE.POST,
        data
      );
      this.setDownloadPreview(true);
      this.setProcessing(false);
      if (res.messageType === 'error') {
        this.setDownloadPreview(false);
      }
      return res;
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      this.setProcessing(false);
      this.setDownloadPreview(false);
    }
    return null;
  }

  async downloadLaunchRequest() {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    if (!this.isDownloadPreview) {
      notificationStore.showMessage(ALERT_MESSAGES.PREVIEW_REQUIRED, 'info');
      return false;
    }
    try {
      this.setProcessing(true);
      const res = await apiRequest('/download/launch', REQUEST_TYPE.PUT, {
        launch: true,
      });

      this.showOutput('Download launch', {});
      this.setDownloadPreview(false);
      this.setProcessing(false);
      return res;
    } catch (error) {
      this.setProcessing(false);
      this.setDownloadPreview(false);
    }
  }

  async searchDownloadRequest(paths: string[]) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }

    try {
      this.setProcessing(true);
      const res = await apiRequest(
        '/search/launchDownload',
        REQUEST_TYPE.POST,
        { paths: paths }
      );

      if (res.messageType !== 'error') {
        this.showOutput('Search download launch', {});
      }

      this.setProcessing(false);
      return res;
    } catch (error) {
      this.setProcessing(false);
      notificationStore.showMessage('Search download failed.', 'error');
      return { messageType: 'error', message: 'Search download failed.' };
    }
  }

  async sampleManagerCreateOrderRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      return await apiRequest(
        '/sample-manager/orders',
        REQUEST_TYPE.POST,
        data
      );
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      return null;
    } finally {
      this.setProcessing(false);
    }
  }

  async sampleManagerTrackingRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      return await apiRequest(
        '/sample-manager/tracking',
        REQUEST_TYPE.POST,
        data
      );
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      return null;
    } finally {
      this.setProcessing(false);
    }
  }

  async sampleManagerTrackingSlidesRequest(sampleId: string) {
    try {
      return await apiRequest(
        '/sample-manager/tracking/slides',
        REQUEST_TYPE.POST,
        { sampleId }
      );
    } catch (error) {
      return null;
    }
  }

  async sampleManagerAutoTriggeringRequest(data: any) {
    if (this.isProcessing) {
      notificationStore.showMessage(ALERT_MESSAGES.PROCESSING, 'info');
      return false;
    }
    this.setProcessing(true);
    try {
      return await apiRequest(
        '/sample-manager/auto-triggering',
        REQUEST_TYPE.POST,
        data
      );
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
      return null;
    } finally {
      this.setProcessing(false);
    }
  }

  async getConfigurationByGroup(group: string) {
    if (this.isLoading) {
      return false;
    }

    if (!AuthStore.selectedGroup) {
      return;
    }

    try {
      this.setLoading(true);
      const res = await apiRequest(
        `/user/configuration/${group}`,
        REQUEST_TYPE.GET
      );

      if (res.response.config) {
        this.setConfig(res.response.config);
      } else {
        this.setConfig(null);
      }
    } catch (error) {
      notificationStore.showMessage(ALERT_MESSAGES.ERROR_PROCESSING, 'error');
    }
    this.setLoading(false);
  }

  async getUserInternalInfo() {
    if (this.isGettingInfo === true) {
      return;
    }
    this.setIsGettingInfo(true);
    try {
      const res = await apiRequest('/user/info', REQUEST_TYPE.GET);
      if (res.messageType !== 'error') {
        this.setInternalUserInfo(res);
      }
    } catch (e) {
      this.setInternalUserInfo(null);
      window.location.href = '/login';
    }

    this.setIsGettingInfo(false);
  }

  async getStatusJobForDownload(jobId: string) {
    try {
      const res = await apiRequest(
        `/download/launchDownload/status/${jobId}`,
        REQUEST_TYPE.GET
      );

      if (res.messageType !== 'error') {
        if (res.response.status === 'ready') {
          const resDownload = await apiRequest(
            `/download/launchDownload/result/${res.response.download_id}`,
            REQUEST_TYPE.GET,
            {},
            { responseType: 'blob' }
          );

          if (resDownload.blob) {
            const url = window.URL.createObjectURL(resDownload.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = resDownload.filename;
            link.click();
            link.remove();
          }

          return res.response;
        }
        return res.response;
      }
      return null;
    } catch (e) {}
  }
}

export default new DataStore();
